import React, { Fragment } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";

const standards = [
  {
    title: "Authorized Distribution Channels",
    desc:
      "We only source digital gift cards from brand-authorized aggregators, official resellers and direct-issuer partnerships. No grey-market, no unverified third parties.",
  },
  {
    title: "Supplier Due Diligence",
    desc:
      "Every supplier we onboard is screened for regulatory compliance, financial stability, and KYC/AML adherence. Contracts include product-authenticity warranties.",
  },
  {
    title: "Traceability",
    desc:
      "Each code issued on our platform is traceable back to its originating supplier and batch — enabling rapid investigation if any issue is ever reported.",
  },
  {
    title: "Continuous Monitoring",
    desc:
      "Supplier performance is reviewed on delivery success rate, redemption rate, and customer reports. Underperforming suppliers are flagged and removed.",
  },
  {
    title: "Brand Compliance",
    desc:
      "All product listings, logos and terms are used in accordance with brand guidelines and reseller agreements we hold with our supplier network.",
  },
  {
    title: "Secure Code Handling",
    desc:
      "Gift card codes are encrypted at rest and in transit. Access is restricted, audited and logged — codes are only ever decrypted for delivery to the verified purchaser.",
  },
];

const commitments = [
  "No grey-market or unauthorized resellers",
  "Verified supplier onboarding with written agreements",
  "End-to-end code encryption",
  "Fraud & authenticity monitoring on every batch",
  "Prompt replacement of any non-working code",
];

const SupplierStandards = () => {
  return (
    <Fragment>
      <Navbar />

      <section className="gpss-section">
        <div className="gpss-bg-orb gpss-bg-orb-tl" />
        <div className="gpss-bg-orb gpss-bg-orb-br" />

        <div className="gpss-inner">
          <div className="gpss-head">
            <div className="gpss-eyebrow">
              <span className="gpss-eyebrow-dot" />
              OUR SOURCING PROMISE
            </div>
            <h1 className="gpss-title">
              Every code comes from an <span className="gpss-title-accent">authorized source</span>
            </h1>
            <p className="gpss-sub">
              We source digital products through authorized and vetted distribution channels —
              because trust starts long before your order reaches your inbox.
            </p>
          </div>

          {/* HERO CARD */}
          <div className="gpss-hero-card">
            <div className="gpss-hero-left">
              <div className="gpss-hero-mini-eyebrow">WHY IT MATTERS</div>
              <h3>Not all gift card sellers are equal</h3>
              <p>
                The digital gift card industry includes unofficial resellers who trade codes
                acquired through questionable means. These codes often fail at redemption —
                or get deactivated later. We do not participate in that market.
              </p>
              <p>
                Every supplier we work with has a direct or authorized relationship with the
                issuing brand. That's not a marketing claim — it's a contractual one.
              </p>
            </div>
            <div className="gpss-hero-right">
              <div className="gpss-hero-right-label">OUR COMMITMENTS</div>
              <ul>
                {commitments.map((c, i) => (
                  <li key={i}>
                    <span className="gpss-tick">
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                    </span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* STANDARDS GRID */}
          <div className="gpss-grid">
            {standards.map((s, i) => (
              <article key={i} className="gpss-card">
                <div className="gpss-card-glow" aria-hidden="true" />
                <div className="gpss-card-num">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="gpss-card-title">{s.title}</h3>
                <p className="gpss-card-desc">{s.desc}</p>
              </article>
            ))}
          </div>

          {/* CALLOUT */}
          <div className="gpss-callout">
            <div className="gpss-callout-icon">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true">
                <path d="M12 2L2 7v7c0 5.55 3.84 10.74 10 12 6.16-1.26 10-6.45 10-12V7z" />
              </svg>
            </div>
            <h3>Spotted a problem with a code? <span>We'll fix it.</span></h3>
            <p>
              We stand behind every code we sell. If anything doesn't work, our support team
              will investigate and replace it — typically within hours, not days.
            </p>
            <div className="gpss-callout-buttons">
              <Link to="/support" className="gpss-btn gpss-btn-primary">
                Report an Issue
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>
              <Link to="/rr" className="gpss-btn gpss-btn-ghost">
                Read Refund Policy
              </Link>
            </div>
          </div>
        </div>

        <style>{`
          .gpss-section {
            position: relative;
            background: linear-gradient(180deg, #07060a 0%, #0b0b0c 100%);
            color: #e6e8ec;
            padding: 60px 22px 90px;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            overflow: hidden;
          }
          .gpss-bg-orb {
            position: absolute;
            width: 460px;
            height: 460px;
            border-radius: 50%;
            filter: blur(140px);
            opacity: 0.4;
            pointer-events: none;
          }
          .gpss-bg-orb-tl { background: rgba(212,168,87,0.18); top: -180px; left: -120px; }
          .gpss-bg-orb-br { background: rgba(212,168,87,0.12); bottom: -200px; right: -120px; }

          .gpss-inner { position: relative; max-width: 1200px; margin: 0 auto; z-index: 1; }

          .gpss-head { text-align: center; margin-bottom: 38px; }
          .gpss-eyebrow {
            display: inline-flex; align-items: center; gap: 8px;
            padding: 6px 14px;
            background: rgba(212,168,87,0.1);
            border: 1px solid rgba(212,168,87,0.3);
            color: #d4a857;
            border-radius: 999px;
            font-size: 11px; font-weight: 700; letter-spacing: 2px;
            margin-bottom: 14px;
          }
          .gpss-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: #d4a857; box-shadow: 0 0 10px #d4a857; animation: gpss-pulse 1.6s ease-in-out infinite; }
          @keyframes gpss-pulse { 0%,100% { opacity:1; transform: scale(1); } 50% { opacity:.55; transform: scale(.85); } }
          .gpss-title {
            font-family: "Playfair Display", "Times New Roman", serif;
            font-size: 38px; font-weight: 700; color: #fff; margin: 0 0 12px; line-height: 1.2;
          }
          .gpss-title-accent {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
            font-style: italic; font-weight: 600;
          }
          .gpss-sub { font-size: 15px; color: #9aa0a8; max-width: 700px; margin: 0 auto; line-height: 1.65; }

          /* HERO CARD */
          .gpss-hero-card {
            position: relative;
            background: linear-gradient(135deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.25);
            border-radius: 22px;
            padding: 40px;
            display: grid;
            grid-template-columns: 1.05fr 1fr;
            gap: 36px;
            align-items: center;
            margin-bottom: 38px;
            overflow: hidden;
          }
          .gpss-hero-card::before {
            content: "";
            position: absolute;
            top: -100px;
            right: -100px;
            width: 320px; height: 320px;
            background: radial-gradient(circle, rgba(212,168,87,0.15), transparent 70%);
            pointer-events: none;
          }
          .gpss-hero-left { position: relative; }
          .gpss-hero-mini-eyebrow {
            font-size: 11px; color: #d4a857;
            text-transform: uppercase; letter-spacing: 2px;
            font-weight: 700; margin-bottom: 10px;
          }
          .gpss-hero-left h3 {
            font-family: "Playfair Display", serif;
            font-size: 26px; color: #fff; margin: 0 0 14px; font-weight: 700; line-height: 1.25;
          }
          .gpss-hero-left p { color: #9aa0a8; line-height: 1.7; margin: 0 0 12px; font-size: 14.5px; }
          .gpss-hero-right { position: relative; }
          .gpss-hero-right-label {
            font-size: 11px; color: #d4a857;
            text-transform: uppercase; letter-spacing: 2px;
            font-weight: 700; margin-bottom: 14px;
          }
          .gpss-hero-right ul {
            list-style: none; padding: 0; margin: 0;
            display: flex; flex-direction: column; gap: 8px;
          }
          .gpss-hero-right li {
            display: flex; align-items: flex-start; gap: 12px;
            padding: 12px 14px;
            background: rgba(255,255,255,0.02);
            border: 1px solid rgba(212,168,87,0.15);
            border-radius: 10px;
            color: #c5c8cf;
            font-size: 13.5px;
            line-height: 1.45;
            transition: border-color .2s ease, background .2s ease;
          }
          .gpss-hero-right li:hover {
            border-color: rgba(212,168,87,0.35);
            background: rgba(212,168,87,0.06);
          }
          .gpss-tick {
            width: 20px; height: 20px;
            background: rgba(212,168,87,0.16);
            border: 1px solid rgba(212,168,87,0.32);
            color: #d4a857;
            border-radius: 50%;
            display: inline-flex; align-items: center; justify-content: center;
            flex-shrink: 0; margin-top: 1px;
          }

          /* STANDARDS GRID */
          .gpss-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 18px;
            margin-bottom: 50px;
          }
          .gpss-card {
            position: relative;
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.2);
            border-radius: 18px;
            padding: 28px 24px;
            overflow: hidden;
            transition: transform .25s ease, border-color .25s ease, box-shadow .25s ease;
          }
          .gpss-card:hover {
            transform: translateY(-4px);
            border-color: rgba(212,168,87,0.55);
            box-shadow: 0 16px 36px rgba(212,168,87,0.14);
          }
          .gpss-card-glow {
            position: absolute;
            top: -60px;
            right: -60px;
            width: 160px;
            height: 160px;
            background: radial-gradient(circle, rgba(212,168,87,0.18), transparent 70%);
            opacity: 0;
            transition: opacity .3s ease;
            pointer-events: none;
          }
          .gpss-card:hover .gpss-card-glow { opacity: 1; }
          .gpss-card-num {
            font-family: "Playfair Display", serif;
            font-size: 28px;
            font-weight: 700;
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
            margin-bottom: 12px;
            letter-spacing: 1px;
          }
          .gpss-card-title {
            font-family: "Playfair Display", serif;
            font-size: 18px; color: #fff; margin: 0 0 8px; font-weight: 700;
          }
          .gpss-card-desc { font-size: 13.5px; color: #9aa0a8; line-height: 1.6; margin: 0; }

          /* CALLOUT */
          .gpss-callout {
            position: relative;
            background: linear-gradient(135deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.3);
            border-radius: 22px;
            padding: 50px 30px;
            text-align: center;
            overflow: hidden;
          }
          .gpss-callout::before {
            content: "";
            position: absolute;
            top: -100px;
            left: 50%;
            transform: translateX(-50%);
            width: 500px; height: 500px;
            background: radial-gradient(circle, rgba(212,168,87,0.15), transparent 70%);
            pointer-events: none;
          }
          .gpss-callout-icon {
            position: relative;
            width: 60px; height: 60px;
            margin: 0 auto 16px;
            background: linear-gradient(135deg, rgba(212,168,87,0.2), rgba(212,168,87,0.05));
            border: 1px solid rgba(212,168,87,0.4);
            color: #d4a857;
            border-radius: 16px;
            display: flex; align-items: center; justify-content: center;
          }
          .gpss-callout h3 {
            position: relative;
            font-family: "Playfair Display", serif;
            font-size: 28px; color: #fff; margin: 0 0 10px; font-weight: 700; line-height: 1.25;
          }
          .gpss-callout h3 span {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
            font-style: italic; font-weight: 600;
          }
          .gpss-callout p {
            position: relative;
            color: #9aa0a8; margin: 0 auto 22px; max-width: 580px;
            font-size: 14.5px; line-height: 1.65;
          }
          .gpss-callout-buttons {
            position: relative;
            display: inline-flex; flex-wrap: wrap; gap: 12px; justify-content: center;
          }
          .gpss-btn {
            display: inline-flex; align-items: center; gap: 10px;
            padding: 13px 26px;
            border-radius: 12px;
            font-weight: 800;
            text-decoration: none;
            font-size: 13px;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            transition: transform .2s ease, box-shadow .25s ease, gap .2s ease, background .2s ease;
          }
          .gpss-btn-primary {
            background: linear-gradient(135deg, #f1cf85, #d4a857 50%, #b58a3f);
            color: #0b0b0c;
          }
          .gpss-btn-primary:hover {
            color: #0b0b0c; text-decoration: none;
            transform: translateY(-2px);
            box-shadow: 0 14px 30px rgba(212,168,87,0.35);
            gap: 12px;
          }
          .gpss-btn-ghost {
            background: rgba(255,255,255,0.04);
            color: #d4a857;
            border: 1px solid rgba(212,168,87,0.3);
          }
          .gpss-btn-ghost:hover {
            background: rgba(212,168,87,0.12);
            border-color: rgba(212,168,87,0.55);
            color: #f1cf85;
            text-decoration: none;
            transform: translateY(-2px);
          }

          @media (max-width: 991px) {
            .gpss-hero-card { grid-template-columns: 1fr; padding: 30px; gap: 24px; }
            .gpss-grid { grid-template-columns: repeat(2, 1fr); }
            .gpss-title { font-size: 30px; }
            .gpss-callout h3 { font-size: 24px; }
          }
          @media (max-width: 575px) {
            .gpss-section { padding: 40px 14px 70px; }
            .gpss-hero-card { padding: 24px; }
            .gpss-hero-left h3 { font-size: 22px; }
            .gpss-grid { grid-template-columns: 1fr; gap: 14px; }
            .gpss-card { padding: 22px 18px; }
            .gpss-callout { padding: 36px 20px; }
            .gpss-title { font-size: 24px; }
            .gpss-callout h3 { font-size: 20px; }
          }
        `}</style>
      </section>

      <Footer />
      <Scrollbar />
    </Fragment>
  );
};

export default SupplierStandards;
